/**
 * Sequential ONVIF probe for bulk-add.
 *
 * Temporary ONVIF registration performs the handshake and rejects invalid
 * credentials. Persistent registration retains configuration and reports
 * connection failures in status, so a failed probe must not gate bulk-add.
 * Single-add and deferred authentication use the same transient capability
 * discovery to resolve the camera's RTSP URI.
 *
 * Bulk-add (this helper) mirrors the same protocol: create a transient
 * ONVIF device, poll the studio's state slice for capabilities, then
 * tear down the transient before the caller commits the real one. The
 * helper is sequential because:
 *   - `external.transients` is a per-id slice the studio's
 *     `provider.tsx` populates from the bridge state-sync. Spamming N
 *     transient devices in parallel made it hard to disambiguate
 *     which capabilities belonged to which probe in pre-monorepo
 *     attempts (see studio.client PR #1100 commit `5247bae9` —
 *     'already exists' issues from racing transient lifecycles).
 *   - Sequencing also means the modal-stays-open progress UI
 *     (`bulkAddStatus = configuring(name, i, N)`) can show one
 *     camera at a time, which matches the Figma reference.
 *
 * Per Multi-Add Phase 1 manual QA — bulk-add ONVIF flow.
 * Per `think-in-systems` (ai.knowledge/global): the helper is the only
 * place that touches `getTransientId` + `addDevice` + the poll loop;
 * the caller in `wiring.ts` consumes the discriminated result and
 * uses a successful URI when available without replacing the selected transport.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
import * as Commands from '#~/device/commands/index.js';
import type { Discovery, Discovery$Control } from '#~/device/discovery/index.js';
import {
  describeAddDiscovery,
  describeAddFailure,
  describeOnvifObservation,
  traceCameraAdd
} from '#~/device/add-trace.js';
import type { Credentials } from '@studio/models/auth';
import type { Agent } from '@studio/models/agent';
import type { AppId } from '@studio/models/app';
import { Protocol as ControlProtocol } from '@studio/models/control';
import type { Configuration as OnvifConfig } from '@studio/models/control/onvif';
import { type Device, getTransientId } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import { toConfiguration as videoToConfiguration } from '@studio/models/video';
import type { Uri } from '@studio/models/net';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';
import { addDevice } from '../store/utils.js';

/**
 * Maximum time we wait for the bridge to surface ONVIF capabilities
 * after creating the transient probe device. Mirrors the value used
 * by the pre-monorepo `studio.client` PR #1100 — 10s covers a healthy
 * LAN probe + ONVIF endpoint response + a brief cushion for state
 * sync latency.
 */
const PROBE_TIMEOUT_MS = 10_000;

/**
 * Poll interval while waiting for capabilities. 500ms keeps the loop
 * responsive (visible progress within a half-second of the bridge
 * publishing the capabilities slice) without saturating React's
 * state-sync subscribers.
 */
const POLL_INTERVAL_MS = 500;

/**
 * Settle window after removing the transient device. Gives the bridge
 * a moment to clean up its side of the device record before the caller
 * commits the real device — without this, a re-add of the same camera
 * raced into "already exists" on the bridge (`studio.client` PR #1100
 * commit `5247bae9`). 500ms matches the value PR #1100 settled on as
 * the smallest window that consistently cleared the bridge-side race
 * on slower QA encoders. Per Multi-Add Phase 1 manual QA — VC-A61P
 * "ghost tile" diagnosis: a 300ms settle was insufficient on the
 * user's bridge, leaving the transient device record visible in the
 * room view while the real-device addDevice raced into a URX conflict
 * that surfaced as "0 of 1 sources added".
 */
const CLEANUP_SETTLE_MS = 500;

/**
 * Discriminated result. Callers route success vs failed → prompt/skip.
 *
 * `videoConfig` is typed `unknown` deliberately: this module sits at the
 * boundary between `@studio/models/video` (the protobuf-derived shape
 * `videoToConfiguration` returns) and `#~/device`'s Minimist Settings
 * shape that the bulk-commit candidate consumes. Forcing both sides
 * to agree on one concrete type drags in a wider refactor of the
 * Minimist video union — out of scope for Phase 1. The caller (wiring
 * `runBulkCommit`) re-casts on assignment, with the cast confined to
 * one site so the boundary is greppable.
 */
export type ProbeResult =
  | { kind: 'success'; videoConfig: unknown }
  | { kind: 'failed'; reason: 'timeout' | 'error'; message?: string };

/**
 * Read-only shape this helper expects on the broker / store-sync
 * subscriber the caller hands in. Kept generic (any) so the helper
 * doesn't drag in the proxy/broker types — wiring.ts owns the
 * resolution and this module owns the polling protocol.
 */
export interface TransientsSnapshot {
  /** Returns the latest external.transients map from the studio store. */
  read: () => Readonly<Record<string, unknown>> | undefined;
}

/** Helper-injected dependencies. Kept as a single object so the call site is greppable. */
export interface ProbeDeps {
  /** Room id the device will eventually live in. */
  roomId: Room['id'];
  /** Agent (bridge) id that owns the device. */
  agentId: Agent['id'];
  /** Studio's appId — used by `getTransientId` to disambiguate scopes. */
  appId: AppId;
  /** Auth0 access token for the addDevice / Commands.remove calls. */
  token: string;
  /** Read-only accessor to `state.external.transients` (provider.tsx populates it). */
  transients: TransientsSnapshot;
  /** Raw bridge evidence distinguishes missing publication from a renderer conversion gap. */
  traceBridgeDevice?: (id: Device['id']) => Record<string, unknown>;
}

/**
 * Probe an ONVIF discovery to extract the RTSP URI the camera reports
 * via its capabilities endpoint. Resolves with a `success` carrying the
 * video config when the bridge publishes `transients[id].control.
 * capabilities.discovery.uri` within `PROBE_TIMEOUT_MS`; otherwise
 * resolves with `failed` (no exceptions thrown — the caller decides
 * whether to prompt for credentials or skip).
 *
 * @param discovery — the bulk-add discovery row. Control config is
 *   cloned + credentials are overlaid from the `credentials` arg.
 * @param credentials — credentials to apply to the transient ONVIF
 *   device. Undefined when the first try is "use whatever the
 *   discovery carried" (typically nothing). Defined for the retry
 *   after the user types credentials into the prompt.
 * @param deps — context the helper needs (roomId / agentId / appId /
 *   token / transients snapshot). See `ProbeDeps` doc.
 */
export const probeOnvifDevice = async (
  discovery: Discovery$Control,
  credentials: T_Maybe<Credentials>,
  deps: ProbeDeps
): Promise<ProbeResult> => {
  const transientId = getTransientId(deps.appId);
  const started = Date.now();
  const bridgeSnapshot = () => {
    try {
      return deps.traceBridgeDevice?.(transientId);
    } catch {
      return { snapshotUnavailable: true };
    }
  };
  const trace = (event: string, details: Record<string, unknown> = {}) =>
    traceCameraAdd(event, {
      ...describeAddDiscovery(discovery),
      transientId,
      roomId: deps.roomId,
      targetAgentId: discovery.agentId ?? deps.agentId,
      credentialsSupplied: credentials != null,
      elapsedMs: Date.now() - started,
      ...details
    });
  const cleanup = async () => {
    try {
      await Commands.remove(transientId as Device['id'], deps.token);
      trace('probe.cleanup-complete');
    } catch (error) {
      trace('probe.cleanup-failed', { failure: describeAddFailure(error) });
    }
  };
  trace('probe.start', { timeoutMs: PROBE_TIMEOUT_MS });

  // Build the transient device payload. Control inherits the discovery's
  // ONVIF config plus the (possibly new) credentials. No video aspect —
  // the whole point of the probe is to have the bridge tell us what the
  // RTSP URI is from the ONVIF capabilities response.
  const baseControl = discovery.control as OnvifConfig;
  const controlWithCreds: OnvifConfig = credentials
    ? ({ ...baseControl, credentials } as OnvifConfig)
    : baseControl;

  // Defensive guard: this helper is only meaningful for ONVIF
  // discoveries. The bulk pipeline already filters but the public
  // helper checks too so unit tests can exercise the boundary.
  if (controlWithCreds.protocol !== ControlProtocol.ONVIF) {
    trace('probe.rejected-protocol');
    return { kind: 'failed', reason: 'error', message: 'not-onvif' };
  }

  // The transient device record. Minimist-shaped on the wire — the
  // studio's addDevice helper hands it to Commands.add which builds
  // the URX from the control config alone (`onvif://host:port/`).
  const transientDevice = {
    agentId: (discovery.agentId ?? deps.agentId) as Agent['id'],
    id: transientId as Device['id'],
    name: discovery.name,
    control: controlWithCreds
    // No video here — bridge probes ONVIF capabilities to discover it.
  };

  try {
    // Spawn the transient on the bridge. May reject if the bridge can't
    // talk to the camera at all (unreachable host) — caller treats that
    // as 'failed' and the user can choose to skip.
    await addDevice(deps.roomId, transientDevice.agentId, transientDevice, deps.token);
    trace('probe.add-acknowledged', { bridge: bridgeSnapshot() });
  } catch (error) {
    const message = (error as Error)?.message ?? 'add-transient-rejected';
    trace('probe.add-rejected', { failure: describeAddFailure(error), bridge: bridgeSnapshot() });
    // Try to clean up — best-effort, bridge may not even have a record yet.
    await cleanup();
    return { kind: 'failed', reason: 'error', message };
  }

  // Poll the studio's transients slice for the URI. provider.tsx
  // populates `state.external.transients[transientId]` from the
  // bridge state-sync subscription; the ONVIF driver writes
  // `control.capabilities.discovery.uri` into that slice once the
  // capabilities response arrives.
  const startedAt = Date.now();
  let videoConfig: unknown;
  let previousObservation: string | undefined;
  let polls = 0;
  let sawUnparseableUri = false;

  while (Date.now() - startedAt < PROBE_TIMEOUT_MS) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

    const snapshot = deps.transients.read();
    polls++;
    const observation = describeOnvifObservation(snapshot, transientId);
    const signature = JSON.stringify(observation);
    // Log state transitions, not every polling tick.
    if (signature !== previousObservation) {
      trace('probe.observation', { observation, bridge: bridgeSnapshot() });
      previousObservation = signature;
    }
    // The transient slice's shape is union-deep — narrow with optional
    // chains because the type doesn't expose `capabilities` directly.
    const transient = snapshot?.[transientId] as
      | { control?: { capabilities?: { discovery?: { uri?: unknown } } } }
      | undefined;
    const rawUri = transient?.control?.capabilities?.discovery?.uri;

    if (!rawUri) continue;

    // `videoToConfiguration` parses the URI back into the typed
    // Video.Configuration shape we can stash on a Minimist candidate.
    // `defaults({}, uri)` is the same form mediator.tsx uses for the
    // single-add path — keeps the substitution byte-identical.
    // Cast the merge result to `Uri` — the bridge publishes a fully-
    // formed URI (host + protocol + ...), the lodash defaults call
    // shape just doesn't carry that promise in its return type.
    const merged = defaults({}, rawUri as Record<string, unknown>) as unknown as Uri;
    const parsed = videoToConfiguration(merged);
    if (parsed) {
      videoConfig = parsed as unknown;
      break;
    }
    if (!sawUnparseableUri) trace('probe.uri-unparseable', { observation });
    sawUnparseableUri = true;
    // If the URI didn't parse, keep polling — the bridge sometimes
    // publishes a placeholder slice before the real capabilities land.
  }

  // Always remove the transient — whether we got the URI or timed out.
  // Failure to clean is non-fatal: the bridge will time out the orphan
  // on its own, but doing it eagerly avoids the "already exists" race
  // when the user re-adds the same camera within seconds (PR #1100).
  // Capture the evidence before cleanup deliberately removes the temporary device.
  trace(videoConfig ? 'probe.success' : 'probe.timeout', {
    polls,
    waitMs: Date.now() - startedAt,
    sawUnparseableUri,
    observation: describeOnvifObservation(deps.transients.read(), transientId),
    bridge: bridgeSnapshot()
  });
  await cleanup();
  await new Promise((r) => setTimeout(r, CLEANUP_SETTLE_MS));

  if (videoConfig) {
    return { kind: 'success', videoConfig };
  }
  return { kind: 'failed', reason: 'timeout' };
};

/**
 * Predicate: is this discovery the kind the bulk pipeline needs to
 * probe via ONVIF? True for control-only discoveries whose protocol is
 * ONVIF. False for video-aspect discoveries (NDI / RTSP family /
 * browsermedia) — those skip the probe and commit directly.
 *
 * Exported so `wiring.ts` can branch the per-discovery preflight step
 * without re-deriving the predicate in two places.
 */
export const isOnvifDiscovery = (discovery: Discovery): discovery is Discovery$Control =>
  'control' in discovery &&
  !!discovery.control &&
  (discovery.control as { protocol?: unknown }).protocol === ControlProtocol.ONVIF &&
  // ONVIF discoveries don't carry a video aspect; if a discovery
  // somehow has both, prefer the existing video path (treat as RTSP).
  !('video' in discovery && (discovery as { video?: unknown }).video);
